import type { CurrentSelection } from "../../../../contexts/auditContext";
import { analyticsResultIdFromSelection } from "../../../../models/audit/analyticsTranslator";
import { selectionNamesAttributeTestResult } from "../../../../models/audit/attributeTestTranslator";

export type OpenFoldOut = "attr-test" | "analytics" | "support" | null;

/**
 * Which inline fold-out (if any) the current selection opens. Single source of
 * truth shared by the shell (`AuditWorkPaperViewV2` hides sibling procedure
 * tables only while a fold-out is actually open), the per-procedure child
 * (`ProcedureSessionWorkPaper` renders the matching fold-out), and the chrome
 * (`AuditWorkpaperLeftPanel` collapses the header to the focus bar).
 *
 * Fold-outs exist only behind the auditor-trail flag. A selection can be set
 * without opening one — e.g. a support cell with no linked document, or a
 * non-attr-test check result (which opens the `CheckAnalysisV2` sheet) — and
 * in those cases the stacked tables must stay visible.
 *
 * The two result kinds answer separately because they open the same fold-out
 * over different evidence, and the caller has to know which: an analytics
 * selection must also suppress the sheet its own namespace would otherwise
 * dispatch to. Each kind is recognised by the id namespace its cell selects
 * under, through the reader that owns that namespace — a cell whose selection
 * this function cannot name opens nothing at all.
 */
export function getOpenFoldOut(
  auditorTrailEnabled: boolean,
  selection: CurrentSelection | undefined,
  showRightPanel: boolean,
): OpenFoldOut {
  if (!auditorTrailEnabled || !selection) return null;
  if (
    selectionNamesAttributeTestResult(
      selection.checkResultId,
      selection.sourceRowId,
    ) &&
    !showRightPanel
  ) {
    return "attr-test";
  }
  // `!showRightPanel` for the same reason the attr-test arm carries it: the
  // legacy support-column side panel is a sibling of the fold-out in the
  // layout, and the two must never be open over one another.
  if (
    !!analyticsResultIdFromSelection(selection.checkResultId) &&
    !showRightPanel
  ) {
    return "analytics";
  }
  if (
    !selection.checkResultId &&
    !!selection.supportColumnId &&
    !!selection.sourceRowId &&
    !!selection.documentId
  ) {
    return "support";
  }
  return null;
}
